import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  ListSubheader,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import { toast } from 'react-toastify';
import { INVOICE_STATUS } from '../../utils/invoiceSchema';
import { InvoiceAmlQuickCheck } from './InvoiceAmlRiskChip';
import { canMarkInvoiceReady, getDraftMissingFieldLabels, isSendReadyDraft } from '../../utils/invoiceFirestoreService';
import { checkInvoiceRecipient } from '../../utils/invoiceEdmApi';
import { fetchRecipientDetails } from '../../utils/recipientHistory';
import { getMukellefDurumu, MUKELLEF_DURUM } from '../../utils/mukellefDurumu';
import { isKrediKartiMatch } from '../../utils/invoiceKrediKartiRule';
import {
  INVOICE_CUSTOMER_TYPES,
  buildManualProductPair,
  getInvoiceProductTypeOptions,
  INVOICE_SCENARIO_TYPES,
  INVOICE_TYPE_OPTIONS,
  applyInvoiceProductTypeOverride,
  applyRecipientCheckToDraft,
  buildSpecialMatrahItemsFromIslemler,
  calculateLineItem,
  createEmptyLineItem,
  isDraftCustomerComplete,
  normalizeInvoiceDraft
} from '../../utils/invoiceDraftUtils';
import { getMatchStatusMeta } from './invoiceStatusUi';
import OfficialInvoiceDialog from './official/OfficialInvoiceDialog';
import { buildOfficialModelForMatch } from '../../utils/officialInvoiceModel';
import { doc as firestoreDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { iv } from './invoiceTheme';
import { toFriendlyEdmMessage } from '../../utils/edmFriendlyMessages';

const hideNumberSpinnersSx = {
  '& input[type=number]': {
    MozAppearance: 'textfield'
  },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0
  }
};

const DENSITY = {
  compact: {
    fieldSx: {
      ...hideNumberSpinnersSx,
      '& .MuiInputBase-input': { fontSize: 12, py: 0.45 },
      '& .MuiInputLabel-root': { fontSize: 11 },
      '& .MuiInputBase-root': { minHeight: 32 }
    },
    sectionTitleSx: {
      fontSize: 10,
      fontWeight: 900,
      color: '#64748b',
      letterSpacing: '0.05em',
      mb: 0.45
    },
    sectionCardSx: { p: 0, border: 'none', bgcolor: 'transparent' },
    bodyPad: { px: 1, py: 0.7, gap: 0.7 },
    headerPad: { px: 1, py: 0.55 },
    footerPad: { px: 1, py: 0.55 },
    headerTitleSize: 12.5,
    metaSize: 11,
    tableHeadSize: 10,
    thSx: { fontWeight: 800, fontSize: 10, py: 0.35, bgcolor: '#f8fafc', lineHeight: 1.15, px: 0.45 , boxSizing: 'border-box' },
    tdSx: { py: 0.3, px: 0.45 , boxSizing: 'border-box' },
    lineInputSize: 11,
    totalsWidth: 420,
    totalsLabelSize: 10,
    totalsValueSize: 12,
    totalsHighlightSize: 13,
    buttonSize: 'small',
    alertMsgSize: 11,
    // Satır arası: outlined etiketler üstteki kutuya değmesin
    gridGap: 1.15,
    showAudit: true
  },
  comfortable: {
    fieldSx: {
      ...hideNumberSpinnersSx,
      '& .MuiInputBase-input': { fontSize: 13.5, py: 0.7 },
      '& .MuiInputLabel-root': { fontSize: 12.5 },
      '& .MuiInputBase-root': { minHeight: 38, borderRadius: '8px' },
      '& .MuiFormHelperText-root': { fontSize: 11 }
    },
    sectionTitleSx: {
      fontSize: 12.5,
      fontWeight: 800,
      color: iv.text,
      letterSpacing: '0.01em',
      mb: 0.55
    },
    sectionCardSx: {
      p: 1,
      border: `1px solid ${iv.border}`,
      borderRadius: '10px',
      bgcolor: '#fff',
      flexShrink: 0
    },
    bodyPad: { px: 1.1, py: 0.75, gap: 0.75 },
    headerPad: { px: 1.25, py: 0.65 },
    footerPad: { px: 1.25, py: 0.65 },
    headerTitleSize: 14.5,
    metaSize: 12.5,
    tableHeadSize: 12,
    thSx: { fontWeight: 800, fontSize: 12, py: 0.55, bgcolor: '#f8fafc', lineHeight: 1.15, px: 0.6 , boxSizing: 'border-box' },
    tdSx: { py: 0.4, px: 0.6 , boxSizing: 'border-box' },
    lineInputSize: 13,
    totalsWidth: 470,
    totalsLabelSize: 11.5,
    totalsValueSize: 13.5,
    totalsHighlightSize: 15.5,
    buttonSize: 'small',
    alertMsgSize: 12.5,
    // Satır arası: outlined etiketler üstteki kutuya değmesin
    gridGap: 1.3,
    showAudit: false,
    fitScreen: true
  }
};

/**
 * Sahis faturasinda Ad / Soyad ayri kutuda tutulur (Kolaysoft ekraniyla ayni).
 * Taslakta tek alan (customerName) sakli; bolme kurali UBL ureticisindekiyle
 * AYNI: son kelime soyad, oncesi ad. Boylece ekranda gorunen ayrim, GİB'e
 * giden <cbc:FirstName>/<cbc:FamilyName> ile birebir ortusuyor.
 */
const splitCustomerName = (fullName) => {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { ad: '', soyad: '' };
  if (parts.length === 1) return { ad: parts[0], soyad: '' };
  return { ad: parts.slice(0, -1).join(' '), soyad: parts[parts.length - 1] };
};

/** 2026 itibariyla bu tutardan sonra alici kimligi zorunlu (GİB). */
/** Sunucudaki gönderim kapısıyla aynı eşik; ayardan okunur (bkz. invoiceSchema). */
const KIMLIK_ZORUNLU_ESIK_TL_VARSAYILAN = 12000;
const kimlikEsigi = (settings) =>
  Number(settings?.kimlikZorunluEsikTl) > 0 ? Number(settings.kimlikZorunluEsikTl) : KIMLIK_ZORUNLU_ESIK_TL_VARSAYILAN;

const formatMoney = (v) =>
  Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function InvoiceDraftDetailPanel({
  match,
  form,
  sube,
  amlStatus,
  onChange,
  onSave,
  onMarkReady,
  onRevertToDraft,
  onSaveReadyEdit,
  onCancelReadyEdit,
  onCancel,
  onDelete,
  onSendToEdm,
  onRefreshEdmStatus,
  saving,
  statusActionLoading,
  sendLoading,
  saveSuccess,
  invoiceSettings,
  density = 'compact'
}) {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAutoPrint, setPreviewAutoPrint] = useState(false);
  const [convertMenuAnchor, setConvertMenuAnchor] = useState(null);
  // Onayli faturada son rotus: "Duzenle" ile acilir, kaydedilince onayli kalir.
  const [readyEdit, setReadyEdit] = useState(false);
  useEffect(() => {
    setReadyEdit(false);
    setHizliTip('');
    setHizliTutar('');
  }, [match?.id]);

  // Ad / Soyad kutulari yazilani AYNEN tutar: her tusta kirpip birlestirince
  // kelime arasindaki bosluk siliniyordu ("BEJ YAPI" yazilamiyordu, 06.10.2026).
  // Birlesik ad (customerName) disaridan degisirse (mukellef sorgusu vb.) kutular yenilenir.
  const [adKutu, setAdKutu] = useState({ ad: '', soyad: '', yazilan: null });
  const birlesikAd = String(form?.customerName || form?.title || '');
  useEffect(() => {
    setAdKutu((onceki) => {
      if (onceki.yazilan !== null && onceki.yazilan === birlesikAd) return onceki;
      const p = splitCustomerName(birlesikAd);
      return { ad: p.ad, soyad: p.soyad, yazilan: birlesikAd };
    });
  }, [birlesikAd, match?.id]);
  // Manuel fatura hizli ekleme: urun tipi + odenecek tutar → urun + iscilik satiri
  const [hizliTip, setHizliTip] = useState('');
  const [hizliTutar, setHizliTutar] = useState('');
  /**
   * Satır sayı kutularında yazılmakta olan ham metin.
   *
   * SORUN: her tuşta calculateLineItem çalışıp değeri sayıya çeviriyordu.
   * Kutudaki "1"i silince değer 0 oluyor, kutuda "0" beliriyor ve personel
   * istediği rakamı yazamıyordu. Kutu boşken burada '' tutulur, hesap 0 ile
   * yapılır; odak çıkınca ham metin bırakılır.
   */
  const [rawLineEdits, setRawLineEdits] = useState({});

  const [recipientChecking, setRecipientChecking] = useState(false);

  /**
   * Resmi görünüm: Kolaysoft'tan kesilmiş kayıtlarda fatura no / ETTN / tarih arşivden
   * okunur (kolaysoftFaturalari/{ETTN}); okunamazsa kayıttaki özet kullanılır.
   */
  const kolaysoftEttn = match?.externalInvoice?.provider === 'kolaysoft' ? match.externalInvoice.ettn : '';
  const [archiveState, setArchiveState] = useState({ ettn: '', doc: null });
  useEffect(() => {
    if (!previewOpen || !kolaysoftEttn) return undefined;
    let cancelled = false;
    getDoc(firestoreDoc(db, 'kolaysoftFaturalari', kolaysoftEttn))
      .then((snap) => !cancelled && setArchiveState({ ettn: kolaysoftEttn, doc: snap.exists() ? snap.data() : null }))
      .catch(() => !cancelled && setArchiveState({ ettn: kolaysoftEttn, doc: null }));
    return () => {
      cancelled = true;
    };
  }, [previewOpen, kolaysoftEttn]);
  const archiveLoading = Boolean(previewOpen && kolaysoftEttn && archiveState.ettn !== kolaysoftEttn);
  const archiveDoc = kolaysoftEttn && archiveState.ettn === kolaysoftEttn ? archiveState.doc : null;
  const officialModel = useMemo(
    () =>
      previewOpen && match && form
        ? buildOfficialModelForMatch({ match, form, settings: invoiceSettings || {}, archiveDoc })
        : null,
    [previewOpen, match, form, invoiceSettings, archiveDoc]
  );

  // Başka bir faturaya geçilince yarım kalan metinler taşınmasın.
  useEffect(() => {
    setRawLineEdits({});
  }, [match?.id]);
  const ui = DENSITY[density] || DENSITY.compact;
  const comfortable = density === 'comfortable';
  // GONDERIM_SEKLI yalniz e-Arsiv faturasinda var; e-Faturada alan gosterilmiyor.
  const gonderimSecilebilir = (form?.scenarioType || 'eArsiv') !== 'eFatura';
  // Telefonda 5-6 sutunluk form alanlari 40px'e dusup okunmaz oluyordu.
  const isPhone = useMediaQuery('(max-width:560px)');
  const phoneCols = (cols) => (isPhone ? 'repeat(2, minmax(0, 1fr))' : cols);
  const missingLabels = useMemo(() => getDraftMissingFieldLabels(form), [form]);
  const totals = form?.invoiceTotals || {};
  const isLocalOnly = Boolean(match?._localOnly) || String(match?.id || '').startsWith('unsaved__');

  const sourceIslemler = useMemo(() => {
    if (!match) return [];
    if (match.matchType === 'multi') {
      return (match.islemlerData || []).filter((row) => row?.id);
    }
    if (match.islemData?.id) return [match.islemData];
    return [];
  }, [match]);

  /*
   * TCKN/VKN girilince bu aliciya daha once kesilen son faturadan vergi
   * dairesi / adres / il-ilce BOS alanlara doldurulur (Kolaysoft bu bilgiyi
   * vermiyor). Dolu alana dokunulmaz.
   */
  const [gecmisNotu, setGecmisNotu] = useState('');
  const aliciNo = String(form?.vknTckn || '').replace(/\D/g, '');
  // Bekleme sirasinda girilen degisiklikler kaybolmasin: en guncel form/onChange
  const formRef = useRef(form);
  formRef.current = form;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const st = String(match?.invoiceStatus || '');
  const gecmisDoldurulabilir =
    Boolean(form) &&
    (st === 'draft' || st === 'error' || (st === 'ready' && readyEdit) || !st);
  useEffect(() => {
    setGecmisNotu('');
    if (!gecmisDoldurulabilir || (aliciNo.length !== 10 && aliciNo.length !== 11)) return undefined;
    let iptal = false;
    const t = setTimeout(async () => {
      try {
        const [d, mukellef] = await Promise.all([
          fetchRecipientDetails(aliciNo).catch(() => null),
          getMukellefDurumu(aliciNo).catch(() => null)
        ]);
        if (iptal) return;
        const form = formRef.current || {};
        const patch = {};
        // Bilinen e-Fatura mukellefi → senaryo e-Fatura (Canli Banka ile ayni kaynak)
        if (mukellef?.durum === MUKELLEF_DURUM.EFATURA && form.scenarioType !== 'eFatura') {
          patch.scenarioType = 'eFatura';
          patch.recipientCheck = { ...(form.recipientCheck || {}), isEfatura: true, identifier: aliciNo, source: 'mukellefDurumu' };
        }
        if (!d && !Object.keys(patch).length) return;
        if (d?.taxOffice && !String(form.taxOffice || form.customerTaxOffice || '').trim()) {
          patch.taxOffice = d.taxOffice;
          patch.customerTaxOffice = d.taxOffice;
        }
        if (d?.street && !String(form.address || form.customerAddress || '').trim()) {
          patch.address = d.street;
          patch.customerAddress = d.street;
          if (d.city) Object.assign(patch, { city: d.city, customerIl: d.city });
          if (d.district) Object.assign(patch, { district: d.district, customerIlce: d.district });
        }
        if (Object.keys(patch).length) {
          onChangeRef.current?.(normalizeInvoiceDraft({ ...form, ...patch }, invoiceSettings));
          const k = d?.kaynak || {};
          const alanlar = Object.keys(patch)
            .filter((x) => ['taxOffice', 'address', 'city', 'district'].includes(x))
            .map((x) => ({ taxOffice: 'vergi dairesi', address: 'adres', city: 'il', district: 'ilçe' }[x]));
          const parcalar = [];
          if (patch.scenarioType === 'eFatura') parcalar.push('Alıcı e-Fatura mükellefi → senaryo e-Fatura yapıldı');
          if (alanlar.length) {
            parcalar.push(
              `Önceki faturadan dolduruldu${k.invoiceNumber ? ` (${k.invoiceNumber}` : ''}${
                k.invoiceDate ? `, ${String(k.invoiceDate).split('-').reverse().join('.')})` : k.invoiceNumber ? ')' : ''
              }: ${alanlar.join(', ')}`
            );
          }
          setGecmisNotu(parcalar.join(' · '));
        }
      } catch (_) {
        /* arsiv okunamazsa elle girilir */
      }
    }, 450);
    return () => {
      iptal = true;
      clearTimeout(t);
    };
    // Yalniz alici numarasi degisince bakilir; form her tusta degisir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aliciNo, gecmisDoldurulabilir, match?.id]);

  if (!match || !form) {
    return (
      <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', p: 3 }}>
        <Typography sx={{ fontSize: comfortable ? 15 : 13, color: '#64748b', fontWeight: 700, textAlign: 'center' }}>
          Düzenlemek için soldan bir taslak seçin.
        </Typography>
      </Box>
    );
  }

  const patchForm = (patch) => onChange(normalizeInvoiceDraft({ ...form, ...patch }, invoiceSettings));


  // --- Musteri kimligi: Kolaysoft ekranindaki gibi Ad / Soyad ayri ---------
  const sahisMi = (form.customerType || 'sahis') !== 'firma';
  const adSoyad = adKutu;
  const setAdSoyad = (ad, soyad) => {
    const tam = [String(ad || '').trim(), String(soyad || '').trim()].filter(Boolean).join(' ');
    setAdKutu({ ad, soyad, yazilan: tam });
    patchForm({ customerName: tam, title: tam });
  };
  const kimlikHane = String(form.vknTckn || '').replace(/\D/g, '');
  const kimlikGecerli = sahisMi ? kimlikHane.length === 11 : kimlikHane.length === 10;
  const adTamam = sahisMi
    ? Boolean(String(adSoyad.ad || '').trim() && String(adSoyad.soyad || '').trim())
    : Boolean(String(form.customerName || form.title || '').trim());
  const kimlikUyarisi =
    Number(totals.payableAmount || 0) >= kimlikEsigi(invoiceSettings) && !(kimlikGecerli && adTamam);

  const patchItemsForm = (patch) =>
    onChange(
      normalizeInvoiceDraft(
        {
          ...form,
          ...patch,
          manuallyEditedItems: true,
          autoGeneratedItems: false
        },
        invoiceSettings
      )
    );

  const setField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    patchForm({ [key]: value });
  };

  const updateItem = (index, patch) => {
    const items = [...(form.items || [])];
    items[index] = calculateLineItem({ ...items[index], ...patch });
    patchItemsForm({ items });
  };

  /**
   * GİB mükellef sorgusu — alıcı e-Fatura mükellefi mi?
   * Sonuç senaryoyu ve müşteri tipini otomatik düzeltir.
   */
  const handleRecipientCheck = async () => {
    const taxId = String(form.vknTckn || '').replace(/\D/g, '');
    if (taxId.length !== 10 && taxId.length !== 11) {
      toast.error('Sorgulamak için 10 haneli VKN veya 11 haneli TCKN girin.');
      return;
    }
    setRecipientChecking(true);
    try {
      const result = await checkInvoiceRecipient({ sube, taxId });
      patchForm(applyRecipientCheckToDraft(form, { ...result, ok: true }));
      toast.success(
        result.isEfatura
          ? `e-Fatura mükellefi${result.title ? ` · ${result.title}` : ''}`
          : 'e-Fatura mükellefi değil — e-Arşiv olarak kesilecek'
      );
    } catch (err) {
      toast.error(err?.message || 'Mükellef sorgulanamadı');
    } finally {
      setRecipientChecking(false);
    }
  };

  const lineEditKey = (index, field) => `${index}:${field}`;

  /** Kutuda görünecek değer: yazılmakta olan ham metin varsa o, yoksa kayıtlı değer. */
  const lineNumberValue = (index, field, storedValue) => {
    const raw = rawLineEdits[lineEditKey(index, field)];
    return raw !== undefined ? raw : storedValue ?? '';
  };

  const handleLineNumberChange = (index, field) => (e) => {
    const raw = e.target.value;
    setRawLineEdits((prev) => ({ ...prev, [lineEditKey(index, field)]: raw }));
    updateItem(index, { [field]: raw === '' ? 0 : raw });
  };

  const handleLineNumberBlur = (index, field) => () =>
    setRawLineEdits((prev) => {
      if (prev[lineEditKey(index, field)] === undefined) return prev;
      const next = { ...prev };
      delete next[lineEditKey(index, field)];
      return next;
    });

  const addItem = () => {
    setRawLineEdits({});
    patchItemsForm({ items: [...(form.items || []), createEmptyLineItem()] });
  };

  const removeItem = (index) => {
    // Satır silinince indeksler kayar; yarım kalan metinleri bırakırız.
    setRawLineEdits({});
    const items = (form.items || []).filter((_, i) => i !== index);
    patchItemsForm({ items: items.length ? items : [createEmptyLineItem()] });
  };

  const statusMeta = getMatchStatusMeta(match);
  const isCancelled = match.invoiceStatus === INVOICE_STATUS.CANCELLED;
  const isReadyStatus = isSendReadyDraft(match);
  const isSentStatus =
    match.invoiceStatus === INVOICE_STATUS.SENT || match.invoiceStatus === INVOICE_STATUS.APPROVED;
  const isErrorStatus = match.invoiceStatus === INVOICE_STATUS.ERROR;
  const isSendingStatus = match.invoiceStatus === INVOICE_STATUS.SENDING;
  /*
   * Saglayici kontrolu. EDM birakildi, sistemde tek saglayici Kolaysoft;
   * eski sube kayitlarinda 'edm' kalmis olabilir, ikisi de kabul edilir.
   *
   * Onceden yalniz 'edm' araniyordu: ayarlar Kolaysoft'a gecince "GIB'e
   * Gonder" dugmesi KALICI OLARAK pasif kaldi ve "entegratör anlasmasi
   * acilinca kullanilacak" diye yaniltici bir ipucu gosterdi. Sunucu
   * tarafinda ayni kontrol (invoiceInboundService) ikisini de kabul
   * ediyordu; yalniz arayuz geride kalmisti.
   */
  const SAGLAYICILAR = ['kolaysoft', 'edm'];
  const edmEnabled =
    SAGLAYICILAR.includes(String(invoiceSettings?.provider || '').toLowerCase()) &&
    invoiceSettings?.enabled !== false;
  const canSendToEdm =
    edmEnabled &&
    (isReadyStatus || isErrorStatus || isSendingStatus) &&
    typeof onSendToEdm === 'function';
  const canRefreshStatus =
    edmEnabled &&
    (isSentStatus || isErrorStatus || isSendingStatus) &&
    match?.providerRef?.uuid &&
    typeof onRefreshEdmStatus === 'function';
  const canMarkReady = canMarkInvoiceReady({ ...match, invoiceDraft: form }, null, invoiceSettings);
  const customerComplete = isDraftCustomerComplete(form);
  // Diger subenin taslagi da duzenlenip onaylanabilir (kullanici karari
  // 06.10.2026); kayit sunucu uzerinden, gecmise sube ile yazilir.
  const readOnly = isCancelled || (isReadyStatus && !readyEdit) || isSentStatus || isSendingStatus;
  const canConvertProductType = !readOnly && match.invoiceStatus === INVOICE_STATUS.DRAFT;
  const vknLabel = form.customerType === 'firma' ? 'VKN' : 'TCKN';
  const matrahWarning = (form.items || []).find((item) => item.matrahWarning)?.matrahWarning;
  const autoRuleNote = (form.items || []).find((item) => item.autoRuleNote)?.autoRuleNote;
  const isKrediKartiDraft = isKrediKartiMatch(match);
  // Kredi kartı taslağı da "standalone" biçiminde saklanır ama personel açmadı; "Yeni taslak" sayılmaz.
  const isStandaloneDraft =
    match.matchType === 'standalone' && !isKrediKartiDraft && match.invoiceStatus === INVOICE_STATUS.DRAFT;
  // Hizli ekleme yalniz islemsiz (sifirdan) taslakta: eslesmeli taslakta
  // kalemler islemden uretiliyor.
  const showHizliEkle = !readOnly && match.matchType === 'standalone' && sourceIslemler.length === 0;

  const handleHizliEkle = () => {
    // "15.000" / "15.000,50" (TR) ve "15000.50" girislerinin hepsi kabul edilir.
    const ham = String(hizliTutar).replace(/\s|TL/gi, '');
    const tutar = Number(
      ham.includes(',') ? ham.replace(/\./g, '').replace(',', '.') : /\.\d{1,2}$/.test(ham) ? ham : ham.replace(/\./g, '')
    );
    const sonuc = buildManualProductPair({ productType: hizliTip, amount: tutar, settings: invoiceSettings });
    if (!sonuc.ok) {
      toast.warning(sonuc.message);
      return;
    }
    // Bos baslangic satiri (urun adi ve tutari yok) atilir; dolu satirlara dokunulmaz.
    const dolu = (form.items || []).filter(
      (it) => String(it.productName || '').trim() || Number(it.lineTotal || it.total || it.unitPrice || 0) > 0
    );
    setRawLineEdits({});
    patchItemsForm({ items: [...dolu, ...sonuc.items] });
    setHizliTutar('');
  };

  const handleConvertProductType = (targetType) => {
    setConvertMenuAnchor(null);
    if (!targetType) return;

    // Tutar 1 adet sarrafiyenin has degerine bile yetmiyorsa fatura piyasanin
    // cok altinda bir satis gosterir (13.400 TL'ye 1 Tam = 12.060 TL).
    const sarrafiye = getInvoiceProductTypeOptions(invoiceSettings).find(
      (o) => o.value === targetType && o.group === 'Sarrafiye'
    );
    const hasTl = Number(invoiceSettings?.liveHasSellPrice) || 0;
    const tutar = Number(totals.payableAmount) || 0;
    if (sarrafiye && hasTl > 0 && tutar > 0 && sarrafiye.hasFactor * hasTl > tutar) {
      toast.warning(
        `${tutar.toLocaleString('tr-TR')} TL, 1 adet ${sarrafiye.label} için yetersiz ` +
          `(has değeri ≈ ${Math.round(sarrafiye.hasFactor * hasTl).toLocaleString('tr-TR')} TL). Daha küçük bir ürün seçin.`
      );
      return;
    }

    if (sourceIslemler.length > 0) {
      const overridden = sourceIslemler.map((row) =>
        applyInvoiceProductTypeOverride(
          { ...row, invoiceProductTypeOverride: targetType },
          targetType,
          invoiceSettings
        )
      );
      const items = buildSpecialMatrahItemsFromIslemler(overridden, invoiceSettings);
      onChange(
        normalizeInvoiceDraft(
          {
            ...form,
            items,
            productTypeOverride: targetType,
            autoGeneratedItems: true,
            manuallyEditedItems: false
          },
          invoiceSettings
        )
      );
      return;
    }

    const label =
      getInvoiceProductTypeOptions(invoiceSettings).find((o) => o.value === targetType)?.label || targetType;
    const items = (form.items || []).map((item) => {
      if (item.lineType === 'workmanship') {
        return calculateLineItem({ ...item, productName: `${label} İşçiliği` });
      }
      return calculateLineItem({ ...item, productName: label });
    });
    onChange(
      normalizeInvoiceDraft(
        {
          ...form,
          items: items.length ? items : [createEmptyLineItem()],
          productTypeOverride: targetType,
          autoGeneratedItems: false,
          manuallyEditedItems: true
        },
        invoiceSettings
      )
    );
  };

  const uiStatusChip = isCancelled
    ? { label: statusMeta.label, color: statusMeta.color, sx: statusMeta.sx }
    : isSentStatus
      ? { label: statusMeta.label, color: statusMeta.color, sx: statusMeta.sx }
      : isErrorStatus
        ? { label: 'Hatalı', color: 'error' }
        : isReadyStatus
          ? { label: 'Onaylandı', color: 'success' }
          : isStandaloneDraft && !customerComplete
            ? { label: 'Yeni taslak', color: 'default', sx: { bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 800 } }
            : !customerComplete
              ? { label: 'Bilgi Eksik', color: 'warning' }
              : canMarkReady
                ? { label: 'Onaya Hazır', color: 'success' }
                : { label: 'Taslak', color: 'default', sx: { bgcolor: '#e2e8f0', color: '#475569', fontWeight: 800 } };

  const handleCancelConfirm = async () => {
    const ok = await onCancel?.(cancelReason);
    if (ok !== false) {
      setCancelDialogOpen(false);
      setCancelReason('');
    }
  };

  const handleDiscardConfirm = async () => {
    const ok = await onCancel?.('');
    if (ok !== false) {
      setDiscardDialogOpen(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const ok = await onDelete?.();
    if (ok !== false) {
      setDeleteDialogOpen(false);
    }
  };

  const btnSx = {
    textTransform: 'none',
    fontWeight: 800,
    fontSize: comfortable ? 13 : undefined,
    px: comfortable ? 1.5 : undefined
  };

  const fitScreen = Boolean(ui.fitScreen);

  return (
    <Box className="iv-draft-detail" sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, opacity: isCancelled ? 0.72 : 1 }}>
      <Box
        sx={{
          ...ui.headerPad,
          borderBottom: `1px solid ${iv.border}`,
          bgcolor: isCancelled ? '#f1f5f9' : comfortable ? '#fff' : '#f8fafc',
          flexShrink: 0
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: ui.headerTitleSize, fontWeight: 900, color: iv.text }}>
            {comfortable ? 'Fatura Formu' : 'Fatura Detayı'}
          </Typography>
          <Chip
            size="small"
            color={uiStatusChip.color}
            label={uiStatusChip.label}
            sx={{ fontWeight: 800, height: comfortable ? 24 : 20, fontSize: comfortable ? 12 : undefined, ...uiStatusChip.sx }}
          />
          {match?._digerSube ? (
            <Chip
              size="small"
              label={`${{ kavakli_sube: 'Kavaklı Şube', sefakoy_sube: 'Şefaköy Şube', sefakoy_merkez: 'Şefaköy Merkez' }[match._sube] || match._sube} faturası`}
              title="Ortak liste: başka şubenin faturası. Düzenleme ve onay buradan yapılabilir, geçmişe şubenizle yazılır; iptal o şubeden."
              sx={{ fontWeight: 800, height: comfortable ? 24 : 20, bgcolor: '#ede9fe', color: '#6d28d9' }}
            />
          ) : null}
          <Typography sx={{ fontSize: ui.metaSize, color: iv.textMuted, fontWeight: 700 }}>
            {isKrediKartiDraft
              ? `Kredi Kartı · İşlem ${match.islemData?.islemNo || match.krediKartiIslemId || ''}`
              : match.matchType === 'standalone'
              ? 'Sıfırdan'
              : match.matchType === 'multi'
                ? `Çoklu · ${match.selectedIslemCount || match.islemIds?.length || 0}`
                : `İşlem ${match.islemData?.islemNo || match.islemId}`}
            {' · '}
            <Box component="span" sx={{ color: iv.primary, fontWeight: 900 }}>
              {formatMoney(totals.payableAmount || form.amount)} TL
            </Box>
            {match.externalInvoice?.provider === 'kolaysoft' ? (
              <Box component="span" sx={{ color: '#166534', fontWeight: 800 }}>
                {' · Kolaysoft '}
                {match.externalInvoice.invoiceNumber}
                {match.externalInvoice.invoiceDate
                  ? ` (${String(match.externalInvoice.invoiceDate).split('-').reverse().join('.')})`
                  : ''}
              </Box>
            ) : null}
          </Typography>
          {!isCancelled && missingLabels.length > 0 && fitScreen ? (
            <Typography sx={{ fontSize: 12, color: '#0369a1', fontWeight: 700 }}>
              Doldurulacak: {missingLabels.join(' · ')}
            </Typography>
          ) : null}
          {saveSuccess && fitScreen ? (
            <Typography sx={{ fontSize: 12, color: iv.success, fontWeight: 800 }}>{saveSuccess}</Typography>
          ) : null}
          {/* Risk durumu ve kontrol butonu başlık satırının sağ ucunda */}
          {sube ? (
            <Box sx={{ ml: 'auto', flexShrink: 0 }}>
              <InvoiceAmlQuickCheck
                sube={sube}
                name={form.customerName || form.title}
                taxId={form.vknTckn}
                initialStatus={amlStatus || undefined}
              />
            </Box>
          ) : null}
        </Stack>
        {!fitScreen && !isCancelled && missingLabels.length > 0 ? (
          <Alert
            severity="warning"
            sx={{
              mt: 0.4,
              py: 0,
              px: 1,
              '& .MuiAlert-message': { fontSize: ui.alertMsgSize, fontWeight: 700 },
              '& .MuiAlert-icon': { fontSize: 16, py: 0.3 }
            }}
          >
            Eksik: {missingLabels.join(' · ')}
          </Alert>
        ) : null}
        {!fitScreen && saveSuccess ? (
          <Alert
            severity="success"
            sx={{
              mt: 0.4,
              py: 0,
              px: 1,
              '& .MuiAlert-message': { fontSize: ui.alertMsgSize, fontWeight: 700 }
            }}
          >
            {saveSuccess}
          </Alert>
        ) : null}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: fitScreen ? 'hidden' : 'auto',
          ...ui.bodyPad,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box sx={ui.sectionCardSx}>
          <Typography sx={ui.sectionTitleSx}>
            {comfortable ? '1. Müşteri Bilgileri' : 'MÜŞTERİ BİLGİLERİ'}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: phoneCols('repeat(auto-fit, minmax(118px, 1fr))'),
              gap: ui.gridGap,
              mb: ui.gridGap
            }}
          >
            <TextField select label="Fatura Tipi" size="small" fullWidth disabled={readOnly} value={form.customerType || 'sahis'} onChange={setField('customerType')} sx={ui.fieldSx}>
              {INVOICE_CUSTOMER_TYPES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <TextField label={vknLabel} size="small" fullWidth disabled={readOnly} value={form.vknTckn || ''} onChange={setField('vknTckn')} sx={ui.fieldSx} />
            <TextField label="Ülke" size="small" fullWidth disabled={readOnly} value={form.country || 'Türkiye'} onChange={setField('country')} sx={ui.fieldSx} />
            <TextField label="İl" size="small" fullWidth disabled={readOnly} value={form.city || form.customerIl || ''} onChange={(e) => patchForm({ city: e.target.value, customerIl: e.target.value })} sx={ui.fieldSx} />
            <TextField label="İlçe" size="small" fullWidth disabled={readOnly} value={form.district || form.customerIlce || ''} onChange={(e) => patchForm({ district: e.target.value, customerIlce: e.target.value })} sx={ui.fieldSx} />
            <TextField label="Telefon" size="small" fullWidth disabled={readOnly} value={form.customerPhone || ''} onChange={setField('customerPhone')} sx={ui.fieldSx} />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: phoneCols(sahisMi ? '1fr 1fr 1.5fr 1.1fr' : '2fr 1.5fr 1.1fr'),
              gap: ui.gridGap
            }}
          >
            {sahisMi ? (
              <>
                <TextField label="Adı" size="small" fullWidth disabled={readOnly} value={adSoyad.ad} onChange={(e) => setAdSoyad(e.target.value, adSoyad.soyad)} sx={ui.fieldSx} />
                <TextField label="Soyadı" size="small" fullWidth disabled={readOnly} value={adSoyad.soyad} onChange={(e) => setAdSoyad(adSoyad.ad, e.target.value)} sx={ui.fieldSx} />
              </>
            ) : (
              <TextField label="Unvan" size="small" fullWidth disabled={readOnly} value={form.customerName || form.title || ''} onChange={(e) => patchForm({ customerName: e.target.value, title: e.target.value })} sx={ui.fieldSx} />
            )}
            <TextField label="Adres" size="small" fullWidth disabled={readOnly} value={form.address || form.customerAddress || ''} onChange={(e) => patchForm({ address: e.target.value, customerAddress: e.target.value })} sx={ui.fieldSx} />
            <TextField label="Vergi Dairesi" size="small" fullWidth disabled={readOnly} value={form.taxOffice || form.customerTaxOffice || ''} onChange={(e) => patchForm({ taxOffice: e.target.value, customerTaxOffice: e.target.value })} sx={ui.fieldSx} />
            {gecmisNotu ? (
              <Typography sx={{ gridColumn: '1 / -1', fontSize: 11.5, fontWeight: 700, color: '#0f766e' }}>{gecmisNotu}</Typography>
            ) : null}
          </Box>
          {kimlikUyarisi ? (
            <Alert
              severity="error"
              sx={{
                mt: ui.gridGap,
                py: 0,
                '& .MuiAlert-message': { fontSize: ui.alertMsgSize, fontWeight: 700 },
                '& .MuiAlert-icon': { fontSize: 16, py: 0.3 }
              }}
            >
              2026 yılı itibarıyla, 12.000 TL ve üzeri faturalar için alıcının adı, soyadı ve TC kimlik
              numarası (veya firma adı ve VKN) zorunludur. Bu bilgileri sağlamadan bu tür faturalar kesilemez.
            </Alert>
          ) : null}
        </Box>

        <Box sx={ui.sectionCardSx}>
          <Typography sx={ui.sectionTitleSx}>
            {comfortable ? '2. Fatura Bilgileri' : 'FATURA BİLGİLERİ'}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              // Kutu sayisi 8'e cikinca dar panelde "Duzenlenme Tarihi" gibi
              // etiketler kesiliyordu; sigmayan kutu alt satira kayar.
              gridTemplateColumns: phoneCols('repeat(auto-fit, minmax(118px, 1fr))'),
              gap: comfortable ? ui.gridGap : 0.45
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
              <TextField select label="Senaryo Türü" size="small" fullWidth disabled={readOnly} value={form.scenarioType || 'eArsiv'} onChange={setField('scenarioType')} sx={{ ...ui.fieldSx, flex: 1, minWidth: 0 }}>
                {INVOICE_SCENARIO_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              {!readOnly ? (
                <Tooltip
                  title={
                    form.recipientCheck
                      ? `GİB sorgusu: ${form.recipientCheck.isEfatura ? 'e-Fatura mükellefi' : 'mükellef değil'}` +
                        `${form.recipientCheck.alias ? ` · etiket ${form.recipientCheck.alias}` : ''}` +
                        `${form.recipientCheck.testMode ? ' · TEST ortamı' : ''}`
                      : 'TCKN/VKN ile GİB mükellef sorgusu yapar, senaryoyu otomatik seçer'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleRecipientCheck}
                      disabled={recipientChecking}
                      aria-label="Mükellef sorgula"
                      sx={{
                        border: `1px solid ${iv.border}`,
                        borderRadius: 1.5,
                        // Sorgu dugmesi diger kutulardan yuksek kalinca
                        // "Senaryo Türü" satiri komsularindan uzun goruniyordu.
                        height: comfortable ? 38 : 32,
                        width: comfortable ? 38 : 32,
                        flexShrink: 0,
                        color: form.recipientCheck
                          ? form.recipientCheck.isEfatura
                            ? iv.success
                            : iv.textMuted
                          : iv.primary
                      }}
                    >
                      {recipientChecking ? <CircularProgress size={15} /> : <SearchIcon sx={{ fontSize: 17 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </Stack>
            <TextField select label="Fatura Türü" size="small" fullWidth disabled={readOnly} value={form.invoiceType || 'ozelMatrah'} onChange={setField('invoiceType')} sx={ui.fieldSx}>
              {INVOICE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            {/* Para Birimi hep TRY; Kolaysoft ekranında da sabit duruyor, bizde de
                değiştirilemez gösteriliyor. Gönderim şekli e-Arşivde GİB'e zorunlu
                bildiriliyor (GONDERIM_SEKLI), e-Faturada böyle bir alan yok — o
                yüzden yalnız e-Arşivde gösteriliyor. */}
            {gonderimSecilebilir ? (
              <TextField
                select
                label="Gönderim Şekli"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form?.sendingType || 'elektronik'}
                onChange={setField('sendingType')}
                sx={ui.fieldSx}
              >
                <MenuItem value="elektronik">Elektronik</MenuItem>
                <MenuItem value="kagit">Kağıt</MenuItem>
              </TextField>
            ) : null}
            <TextField label="Düzenlenme Tarihi" size="small" type="date" fullWidth disabled={readOnly} value={String(form.invoiceDate || '').slice(0, 10)} onChange={setField('invoiceDate')} InputLabelProps={{ shrink: true }} sx={ui.fieldSx} />
            <TextField label="Düzenlenme Saati" size="small" type="time" fullWidth disabled={readOnly} value={form.invoiceTime || '12:00'} onChange={setField('invoiceTime')} InputLabelProps={{ shrink: true }} sx={ui.fieldSx} />
            <TextField select label="XSLT Dosyası" size="small" fullWidth disabled={readOnly} value={form.xsltTemplate || 'gibXslt'} onChange={setField('xsltTemplate')} sx={ui.fieldSx}>
              <MenuItem value="gibXslt">GİB XSLT</MenuItem>
              <MenuItem value="default">Varsayılan</MenuItem>
            </TextField>
            {!comfortable ? (
              <TextField label="Ödeme" size="small" select fullWidth disabled={readOnly} value={form.paymentType || 'Havale/EFT'} onChange={setField('paymentType')} sx={ui.fieldSx}>
                <MenuItem value="Havale/EFT">Havale/EFT</MenuItem>
                <MenuItem value="Nakit">Nakit</MenuItem>
                <MenuItem value="Kredi Kartı">Kredi Kartı</MenuItem>
              </TextField>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            ...ui.sectionCardSx,
            // Sabit yukseklikte sikistirmak yerine icerige gore boy aliyor;
            // panel kisa kalirsa govde kayiyor. Boylece Aciklama kutusu
            // tablonun uzerine binmiyor.
            flex: '0 0 auto',
            display: 'flex',
            gap: comfortable ? 1 : 0.7,
            alignItems: 'stretch',
            // Toplamlar tablonun ALTINA alindi (Kolaysoft ekraninda da orada);
            // yan panelde 9 sutun yan sutunla birlikte sigmiyor, kutularin
            // icindeki rakamlar kesiliyordu.
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.45, flexShrink: 0 }}>
              <Typography sx={{ ...ui.sectionTitleSx, mb: 0 }}>{comfortable ? '3. Ürün Hizmet Bilgileri' : 'ÜRÜN HİZMET BİLGİLERİ'}</Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {canConvertProductType && !showHizliEkle ? (
                  <>
                    <Button
                      size={ui.buttonSize}
                      startIcon={<SwapHorizIcon sx={{ fontSize: comfortable ? 16 : 14 }} />}
                      onClick={(e) => setConvertMenuAnchor(e.currentTarget)}
                      sx={{ ...btnSx, color: iv.text, border: `1px solid ${iv.border}` }}
                      variant="outlined"
                    >
                      Ürün Tipi
                      {form.productTypeOverride
                        ? `: ${
                            getInvoiceProductTypeOptions(invoiceSettings).find((o) => o.value === form.productTypeOverride)
                              ?.label || form.productTypeOverride
                          }`
                        : ''}
                    </Button>
                    <Menu
                      anchorEl={convertMenuAnchor}
                      open={Boolean(convertMenuAnchor)}
                      onClose={() => setConvertMenuAnchor(null)}
                    >
                      {getInvoiceProductTypeOptions(invoiceSettings).flatMap((opt, i, list) => [
                        // Sarrafiye grubunun basina ayrac + baslik
                        opt.group === 'Sarrafiye' && list[i - 1]?.group !== 'Sarrafiye' ? (
                          <ListSubheader
                            key="sarrafiye-baslik"
                            sx={{ fontSize: 11, fontWeight: 800, lineHeight: '28px', color: iv.textMuted, borderTop: `1px solid ${iv.border}` }}
                          >
                            SARRAFİYE
                          </ListSubheader>
                        ) : null,
                        <MenuItem
                          key={opt.value}
                          selected={form.productTypeOverride === opt.value}
                          onClick={() => handleConvertProductType(opt.value)}
                          sx={{ fontWeight: 700, fontSize: 13 }}
                        >
                          {opt.label}
                        </MenuItem>
                      ]).filter(Boolean)}
                    </Menu>
                  </>
                ) : null}
                {!readOnly ? (
                  <Button
                    size={ui.buttonSize}
                    startIcon={<AddIcon sx={{ fontSize: comfortable ? 16 : 14 }} />}
                    onClick={addItem}
                    sx={{ ...btnSx, color: iv.primary }}
                  >
                    Satır Ekle
                  </Button>
                ) : null}
              </Stack>
            </Stack>
            {showHizliEkle ? (
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
                sx={{ mb: 0.75, p: 0.75, borderRadius: '8px', bgcolor: iv.primarySoft, border: `1px solid ${iv.border}` }}
              >
                <TextField
                  select
                  size="small"
                  label="Ürün Tipi"
                  value={hizliTip}
                  onChange={(e) => setHizliTip(e.target.value)}
                  sx={{ width: 230, bgcolor: '#fff' }}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 420 } } } }}
                >
                  {getInvoiceProductTypeOptions(invoiceSettings).flatMap((opt, i, list) => [
                    opt.group === 'Sarrafiye' && list[i - 1]?.group !== 'Sarrafiye' ? (
                      <ListSubheader key="hizli-sarrafiye" sx={{ fontSize: 11, fontWeight: 800, lineHeight: '28px' }}>
                        SARRAFİYE
                      </ListSubheader>
                    ) : null,
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13, fontWeight: 700 }}>
                      {opt.label}
                    </MenuItem>
                  ]).filter(Boolean)}
                </TextField>
                <TextField
                  size="small"
                  label="Ödenecek Tutar (KDV dahil)"
                  value={hizliTutar}
                  onChange={(e) => setHizliTutar(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleHizliEkle();
                    }
                  }}
                  inputProps={{ inputMode: 'decimal' }}
                  InputProps={{ endAdornment: <Typography sx={{ fontSize: 12, color: iv.textMuted }}>TL</Typography> }}
                  sx={{ width: 240, bgcolor: '#fff' }}
                />
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={<AddIcon />}
                  disabled={!hizliTip || !String(hizliTutar).trim()}
                  onClick={handleHizliEkle}
                  sx={{ textTransform: 'none', fontWeight: 800, bgcolor: iv.primary, '&:hover': { bgcolor: iv.primaryHover || iv.primary } }}
                >
                  Ekle
                </Button>
                <Typography sx={{ fontSize: 11.5, color: iv.textMuted }}>
                  Ürün satırı %0 + işçilik satırı %20 otomatik açılır.
                </Typography>
              </Stack>
            ) : null}
            <Box
              sx={{
                // Cok satirli faturada tablo kendi icinde kayar; az satirda
                // bosuna yer kaplamaz.
                maxHeight: comfortable ? 420 : 300,
                border: `1px solid ${iv.border}`,
                borderRadius: comfortable ? '8px' : 1.2,
                overflow: 'auto'
              }}
            >
              {/* tableLayout: fixed olmadan tarayici sutun genisliklerini
                  basliklara gore buyutuyor ve "Urun / Hizmet" kolonu
                  eziliyordu (urun adlari "Eski Zi..." diye kesiliyordu). */}
              <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: comfortable ? 950 : 730 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ ...ui.thSx, width: comfortable ? 32 : 26 }}>#</TableCell>
                    <TableCell align="center" sx={{ ...ui.thSx, width: comfortable ? 300 : 186 }}>Ürün / Hizmet</TableCell>
                    <TableCell align="center" sx={{ ...ui.thSx, width: comfortable ? 96 : 76 }}>Miktar</TableCell>
                    <TableCell align="center" sx={{ ...ui.thSx, width: comfortable ? 104 : 84 }}>Birim</TableCell>
                    <TableCell align="center" sx={{ ...ui.thSx, width: comfortable ? 120 : 96 }}>Birim Fiyat</TableCell>
                    <TableCell align="center" sx={{ ...ui.thSx, width: comfortable ? 80 : 62 }}>KDV(%)</TableCell>
                    <TableCell align="center" sx={{ ...ui.thSx, width: comfortable ? 88 : 72 }}>KDV(₺)</TableCell>
                    <TableCell align="center" sx={{ ...ui.thSx, width: comfortable ? 104 : 88 }}>Toplam Fiyat</TableCell>
                    {!readOnly ? <TableCell align="center" sx={{ ...ui.thSx, width: comfortable ? 48 : 40 }}>İşlemler</TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(form.items || []).map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell align="center" sx={{ ...ui.tdSx, fontSize: ui.lineInputSize, fontWeight: 700, color: iv.textMuted }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={ui.tdSx}>
                        <TextField size="small" fullWidth disabled={readOnly} value={item.productName || ''} onChange={(e) => updateItem(index, { productName: e.target.value })} sx={{ '& .MuiInputBase-input': { fontSize: ui.lineInputSize, py: comfortable ? 0.55 : 0.3 } }} />
                      </TableCell>
                      <TableCell sx={ui.tdSx}>
                        <TextField size="small" type="number" fullWidth disabled={readOnly} value={lineNumberValue(index, 'quantity', item.quantity ?? '')} onChange={handleLineNumberChange(index, 'quantity')} onBlur={handleLineNumberBlur(index, 'quantity')} sx={{ ...hideNumberSpinnersSx, '& .MuiInputBase-input': { fontSize: ui.lineInputSize, py: comfortable ? 0.55 : 0.3, textAlign: 'center' } }} />
                      </TableCell>
                      <TableCell sx={ui.tdSx}>
                        <TextField select size="small" fullWidth disabled={readOnly} value={String(item.unit || 'ADET').toUpperCase() === 'GRAM' ? 'GRAM' : 'ADET'} onChange={(e) => updateItem(index, { unit: e.target.value })} sx={{ '& .MuiInputBase-input': { fontSize: ui.lineInputSize, py: comfortable ? 0.55 : 0.3, pl: 0.8, pr: '22px !important' }, '& .MuiSelect-icon': { right: 0 } }}>
                          <MenuItem value="ADET">Adet</MenuItem>
                          <MenuItem value="GRAM">Gram</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell sx={ui.tdSx}>
                        <TextField size="small" type="number" fullWidth disabled={readOnly} value={lineNumberValue(index, 'unitPrice', item.unitPrice ?? '')} onChange={handleLineNumberChange(index, 'unitPrice')} onBlur={handleLineNumberBlur(index, 'unitPrice')} sx={{ ...hideNumberSpinnersSx, '& .MuiInputBase-input': { fontSize: ui.lineInputSize, py: comfortable ? 0.55 : 0.3, textAlign: 'center' } }} />
                      </TableCell>
                      <TableCell sx={ui.tdSx}>
                        <TextField size="small" type="number" fullWidth disabled={readOnly} value={lineNumberValue(index, 'vatRate', item.vatRate ?? 0)} onChange={handleLineNumberChange(index, 'vatRate')} onBlur={handleLineNumberBlur(index, 'vatRate')} sx={{ ...hideNumberSpinnersSx, '& .MuiInputBase-input': { fontSize: ui.lineInputSize, py: comfortable ? 0.55 : 0.3, textAlign: 'center' } }} />
                      </TableCell>
                      <TableCell align="center" sx={{ ...ui.tdSx, fontSize: ui.lineInputSize, whiteSpace: 'nowrap', color: iv.textMuted }}>
                        {formatMoney(calculateLineItem(item).vatAmount)}
                      </TableCell>
                      <TableCell align="center" sx={{ ...ui.tdSx, fontSize: ui.lineInputSize, fontWeight: 800, whiteSpace: 'nowrap' }}>
                        {formatMoney(item.lineTotal)}
                      </TableCell>
                      {!readOnly ? (
                        <TableCell align="center" sx={{ ...ui.tdSx, py: comfortable ? 0.25 : 0.15 }}>
                          <IconButton size="small" color="error" onClick={() => removeItem(index)} disabled={(form.items || []).length <= 1}>
                            <DeleteOutlineIcon sx={{ fontSize: comfortable ? 18 : 15 }} />
                          </IconButton>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {matrahWarning ? (
              <Alert
                severity="info"
                sx={{
                  mt: 0.45,
                  py: 0,
                  flexShrink: 0,
                  '& .MuiAlert-message': { fontSize: comfortable ? 11.5 : 10.5, fontWeight: 600 },
                  '& .MuiAlert-icon': { fontSize: 15, py: 0.25 }
                }}
              >
                {matrahWarning}
              </Alert>
            ) : null}
            {autoRuleNote ? (
              <Alert
                severity="success"
                sx={{
                  mt: 0.45,
                  py: 0,
                  flexShrink: 0,
                  '& .MuiAlert-message': { fontSize: comfortable ? 11.5 : 10.5, fontWeight: 600 },
                  '& .MuiAlert-icon': { fontSize: 15, py: 0.25 }
                }}
              >
                {autoRuleNote}
              </Alert>
            ) : null}
          </Box>

        </Box>

        {/* alignItems: stretch -> Aciklama kutusu sagindaki Toplamlar blogu ile
            ayni yuksekligi alir. mt: tablo ile arasinda nefes payi. */}
        <Box
          sx={{
            display: 'flex',
            gap: comfortable ? 1 : 0.7,
            alignItems: 'stretch',
            flexShrink: 0,
            mt: comfortable ? 1.4 : 1.1
          }}
        >
          <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex' }}>
            <TextField
              label="Açıklama"
              size="small"
              fullWidth
              multiline
              disabled={readOnly}
              value={form.invoiceNote || ''}
              onChange={setField('invoiceNote')}
              sx={{
                ...ui.fieldSx,
                flex: 1,
                // Yuksekligi komsu bloktan alsin; metin uzunsa kendi icinde kayar.
                '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
                '& textarea': { height: '100% !important', overflow: 'auto !important' }
              }}
            />
          </Box>
          <Box
            sx={{
              width: ui.totalsWidth,
              flexShrink: 0,
              // Yedi satir alt alta durunca tablonun yuksekligini yiyordu;
              // uc sutuna alininca uc satira iniyor.
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              alignContent: 'start',
              gap: comfortable ? 0.45 : 0.35
            }}
          >
            <Typography sx={{ ...ui.sectionTitleSx, mb: 0, gridColumn: '1 / -1' }}>
              {comfortable ? 'Toplamlar' : 'TOPLAMLAR'}
            </Typography>
            {/* Sıra ve adlandırma Kolaysoft'un yeni fatura ekranıyla aynı; tek fark
                "Özel Matrah 805" satırı — KDV'nin hesaplandığı işçilik neti bizde
                ayrıca görünmeli. "Ürün/Hizmet Tutar" KDV HARİÇtir; satır
                tablosundaki "Toplam Fiyat" ise KDV dahildir. */}
            {[
              { label: 'Ürün/Hizmet Tutar', value: totals.goodsServiceAmount },
              { label: 'Özel Matrah 805', value: totals.ozelMatrahAmount },
              { label: 'KDV (%0)', value: totals.vat0Amount || 0 },
              { label: 'KDV (%20)', value: totals.vat20Amount },
              { label: 'Vergiler Hariç Tutar', value: totals.taxExclusiveAmount },
              { label: 'Vergiler Dahil Tutar', value: totals.taxInclusiveAmount },
              { label: 'Ödenecek Tutar', value: totals.payableAmount, highlight: true }
            ].map((row) => (
              <Box
                key={row.label}
                sx={{
                  px: comfortable ? 0.9 : 0.75,
                  py: comfortable ? 0.5 : 0.4,
                  borderRadius: comfortable ? '8px' : 1.2,
                  border: `1px solid ${iv.border}`,
                  bgcolor: row.highlight ? iv.primarySoft : '#f8fafc',
                  // Etiket ustte, rakam altta: "Vergiler Hariç Tutar" gibi uzun
                  // etiketlerde yan yana dizilim rakami kesiyordu.
                  display: 'flex',
                  flexDirection: row.highlight ? 'row' : 'column',
                  alignItems: row.highlight ? 'baseline' : 'stretch',
                  justifyContent: row.highlight ? 'space-between' : 'flex-start',
                  gap: row.highlight ? 0.5 : 0,
                  ...(row.highlight ? { gridColumn: '1 / -1' } : null)
                }}
              >
                <Typography sx={{ fontSize: ui.totalsLabelSize, color: iv.textMuted, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {row.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: row.highlight ? ui.totalsHighlightSize : ui.totalsValueSize,
                    fontWeight: row.highlight ? 900 : 800,
                    color: row.highlight ? iv.primary : iv.text,
                    whiteSpace: 'nowrap',
                    textAlign: row.highlight ? 'right' : 'left'
                  }}
                >
                  {formatMoney(row.value)} TL
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

      </Box>

      <Box sx={{ ...ui.footerPad, borderTop: `1px solid ${iv.border}`, bgcolor: comfortable ? '#fff' : '#f8fafc', flexShrink: 0 }}>
        <Stack direction="row" spacing={comfortable ? 1 : 0.55} flexWrap="wrap" useFlexGap>
          <Button size={ui.buttonSize} variant="outlined" startIcon={<VisibilityIcon />} onClick={() => { setPreviewAutoPrint(false); setPreviewOpen(true); }} sx={btnSx}>Önizle</Button>
          <Button size={ui.buttonSize} variant="outlined" startIcon={<PrintIcon />} onClick={() => { setPreviewAutoPrint(true); setPreviewOpen(true); }} sx={btnSx}>Yazdır</Button>
          {!isCancelled ? (
            <>
              {match.invoiceStatus === INVOICE_STATUS.DRAFT ? (
                <Button size={ui.buttonSize} variant="contained" disabled={saving || statusActionLoading} startIcon={saving ? <CircularProgress size={comfortable ? 16 : 12} color="inherit" /> : <SaveIcon />} onClick={onSave} sx={{ ...btnSx, bgcolor: '#1d4ed8' }}>Kaydet</Button>
              ) : null}
              {match.invoiceStatus === INVOICE_STATUS.DRAFT && canMarkReady && !isLocalOnly ? (
                <Button size={ui.buttonSize} variant="contained" disabled={statusActionLoading} startIcon={statusActionLoading ? <CircularProgress size={comfortable ? 16 : 12} color="inherit" /> : <SendIcon />} onClick={onMarkReady} sx={{ ...btnSx, bgcolor: '#059669' }}>Onaya Gönder</Button>
              ) : null}
              {isReadyStatus && onSaveReadyEdit && !readyEdit ? (
                <Button size={ui.buttonSize} variant="outlined" disabled={statusActionLoading || sendLoading} onClick={() => setReadyEdit(true)} sx={{ ...btnSx, color: '#1d4ed8', borderColor: '#1d4ed8' }}>Düzenle</Button>
              ) : null}
              {isReadyStatus && readyEdit ? (
                <>
                  <Button
                    size={ui.buttonSize}
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={comfortable ? 16 : 12} color="inherit" /> : <SaveIcon />}
                    onClick={async () => {
                      const ok = await onSaveReadyEdit?.(form);
                      if (ok) setReadyEdit(false);
                    }}
                    sx={{ ...btnSx, bgcolor: '#1d4ed8' }}
                  >
                    Kaydet
                  </Button>
                  <Button
                    size={ui.buttonSize}
                    variant="outlined"
                    disabled={saving}
                    onClick={() => {
                      setReadyEdit(false);
                      onCancelReadyEdit?.();
                    }}
                    sx={btnSx}
                  >
                    Vazgeç
                  </Button>
                </>
              ) : null}
              {isReadyStatus && onRevertToDraft && !readyEdit ? (
                <Button size={ui.buttonSize} variant="outlined" disabled={statusActionLoading} startIcon={<UndoIcon />} onClick={onRevertToDraft} sx={btnSx}>Onayı Geri Al</Button>
              ) : null}
              {canSendToEdm && !readyEdit ? (
                <Button
                  size={ui.buttonSize}
                  variant="contained"
                  disabled={statusActionLoading || sendLoading}
                  startIcon={sendLoading ? <CircularProgress size={comfortable ? 16 : 12} color="inherit" /> : <ReceiptLongIcon />}
                  onClick={onSendToEdm}
                  sx={{ ...btnSx, bgcolor: '#0f766e' }}
                >
                  GİB&apos;e Gönder
                </Button>
              ) : isReadyStatus && !edmEnabled ? (
                <Tooltip title="Entegratör anlaşması ve Ayarlar’da sağlayıcı + GİB entegrasyonu açılınca kullanılacak.">
                  <span>
                    <Button size={ui.buttonSize} variant="outlined" disabled startIcon={<ReceiptLongIcon />} sx={{ ...btnSx, color: '#64748b' }}>
                      GİB&apos;e Gönder
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
              {canRefreshStatus ? (
                <Button
                  size={ui.buttonSize}
                  variant="outlined"
                  disabled={statusActionLoading || sendLoading}
                  onClick={onRefreshEdmStatus}
                  sx={btnSx}
                >
                  Durumu Yenile
                </Button>
              ) : null}
              {match.providerError ? (
                <Typography sx={{ fontSize: comfortable ? 13 : 11, color: '#b91c1c', fontWeight: 700, alignSelf: 'center' }}>
                  {toFriendlyEdmMessage(match.providerError)}
                </Typography>
              ) : null}
              {/* Baska subenin faturasi yalniz gonderilir; iptal o subeden */}
              {!match?._digerSube ? (
              <Button
                size={ui.buttonSize}
                variant="outlined"
                color="error"
                disabled={statusActionLoading || isSentStatus}
                startIcon={<CancelOutlinedIcon />}
                onClick={() => (isLocalOnly ? setDiscardDialogOpen(true) : setCancelDialogOpen(true))}
                sx={btnSx}
              >
                İptal
              </Button>
              ) : null}
              {(match.auditTrail || []).length > 0 ? (
                <Button
                  size={ui.buttonSize}
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => setHistoryDialogOpen(true)}
                  sx={btnSx}
                >
                  İşlem Geçmişi
                </Button>
              ) : null}
            </>
          ) : (
            <>
              <Button size={ui.buttonSize} variant="contained" color="error" disabled={statusActionLoading} startIcon={statusActionLoading ? <CircularProgress size={comfortable ? 16 : 12} color="inherit" /> : <DeleteForeverIcon />} onClick={() => setDeleteDialogOpen(true)} sx={btnSx}>Sil</Button>
              {(match.auditTrail || []).length > 0 ? (
                <Button size={ui.buttonSize} variant="outlined" startIcon={<HistoryIcon />} onClick={() => setHistoryDialogOpen(true)} sx={btnSx}>
                  İşlem Geçmişi
                </Button>
              ) : null}
            </>
          )}
        </Stack>
      </Box>

      <OfficialInvoiceDialog
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewAutoPrint(false); }}
        model={officialModel}
        loading={archiveLoading}
        autoPrint={previewAutoPrint}
      />

      {/* Islem gecmisi ekranda yer kapliyordu; artik butonla acilan kucuk
          pencerede. Kayitlarda bir degisiklik yok, yalniz gosterim yeri. */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>İşlem Geçmişi</DialogTitle>
        <DialogContent dividers>
          {[...(match.auditTrail || [])].reverse().map((ev) => {
            const who = [ev?.by?.isim, ev?.by?.soyisim].filter(Boolean).join(' ') || 'Sistem';
            const when = ev?.at
              ? new Date(ev.at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
              : '—';
            return (
              <Box key={ev.id || `${ev.at}-${ev.type}`} sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a' }}>
                  {when} · {who}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: '#64748b', fontWeight: 600 }}>
                  {toFriendlyEdmMessage(ev.detail || ev.type)}
                </Typography>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 800 }}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Kaydetmeden çıkılsın mı?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13.5, color: '#475569' }}>
            Kaydetmeden çıkmak istiyor musunuz? Yazdığınız bilgiler kaydedilmez.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Vazgeç
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={statusActionLoading}
            onClick={handleDiscardConfirm}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Kaydetmeden çık
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Taslağı İptal Et</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13, mb: 1.5, color: '#475569' }}>Bu taslak iptal edilecek. Kayıt silinmez; aynı banka hareketi ve işlem için yeniden eşleştirme yapılabilir.</Typography>
          <TextField label="İptal Nedeni" fullWidth multiline minRows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="İptal nedenini yazın..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} sx={{ textTransform: 'none' }}>Vazgeç</Button>
          <Button color="error" variant="contained" disabled={!cancelReason.trim() || statusActionLoading} onClick={handleCancelConfirm} sx={{ textTransform: 'none', fontWeight: 800 }}>İptal Et</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>İptal Edilen Taslak Silinsin mi?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13, color: '#475569' }}>
            Bu işlem yalnızca iptal edilmiş fatura taslağını siler. Banka hareketi ve işlem kaydı silinmez.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none' }}>Vazgeç</Button>
          <Button color="error" variant="contained" disabled={statusActionLoading} onClick={handleDeleteConfirm} sx={{ textTransform: 'none', fontWeight: 800 }}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InvoiceDraftDetailPanel;
